import React, { useEffect, useRef, useState } from 'react';
import {
    Star,
    Play,
    Clock,
    CheckCircle,
    XCircle,
    Edit,
    Trash2,
    Calendar,
    FileText,
    BarChart3,
    Table,
    Loader2,
    Eye,
    LayoutDashboard,
    MoreHorizontal,
} from 'lucide-react';
import { SavedReportDTO } from '../../../../../services/claudeQueryService';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { viewFromChartConfig } from '../view/types';

interface ReportCardProps {
    report: SavedReportDTO;
    onRun: (id: number) => void;
    onEdit: (report: SavedReportDTO) => void;
    onDelete: (id: number) => void;
    onToggleFavorite: (id: number, isFavorite: boolean) => void;
    isRunning?: boolean;
    /** Åpne rapporten med visningen (ferske data) */
    onOpen?: (report: SavedReportDTO) => void;
}

/** Beskrivelsen vises ikke når den bare gjentar tittelen («Pinnet fra Compass: <tittel>»). */
function usefulDescription(report: SavedReportDTO): string | undefined {
    const description = report.description?.trim();
    if (!description) return undefined;
    const stripped = description.replace(/^(Pinnet fra Compass|Auto-generert fra dashboard):\s*/i, '').trim().toLocaleLowerCase('nb-NO');
    const name = report.name.trim().toLocaleLowerCase('nb-NO').replace(/\.\.\.$/, '');
    return stripped === name || (name.length >= 20 && stripped.startsWith(name)) ? undefined : description;
}

export const ReportCard: React.FC<ReportCardProps> = ({
    report,
    onRun,
    onEdit,
    onDelete,
    onToggleFavorite,
    onOpen,
    isRunning = false
}) => {
    const { t } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!menuOpen) return;
        const close = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
        document.addEventListener('mousedown', close);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('keydown', onKey);
        };
    }, [menuOpen]);

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        return d.toLocaleDateString('nb-NO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusIcon = () => {
        if (!report.lastRunStatus) return null;
        switch (report.lastRunStatus) {
            case 'completed':
                return <CheckCircle size={14} className="text-green-500" />;
            case 'failed':
                return <XCircle size={14} className="text-red-500" />;
            default:
                return <Clock size={14} className="text-yellow-500" />;
        }
    };

    const view = viewFromChartConfig(report.chartConfig);
    const getFormatIcon = () => {
        if (view) return <LayoutDashboard size={16} className="text-blue-500" />;
        switch (report.exportFormat) {
            case 'chart':
                return <BarChart3 size={16} className="text-purple-500" />;
            case 'table':
                return <Table size={16} className="text-blue-500" />;
            default:
                return <FileText size={16} className="text-gray-500" />;
        }
    };

    const description = usefulDescription(report);
    const menuItem = 'w-full flex items-center gap-2 min-h-[44px] px-3 text-sm text-left rounded-md hover:bg-gray-50';

    return (
        <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all p-4 flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between mb-1">
                <div className="flex items-center gap-2 flex-1 min-w-0 pt-2.5">
                    {getFormatIcon()}
                    <h3 className="font-semibold text-gray-800 truncate">{report.name}</h3>
                </div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite(report.reportId, !report.isFavorite);
                    }}
                    className="flex items-center justify-center min-w-[44px] min-h-[44px] -mr-2 flex-shrink-0 rounded-lg hover:bg-gray-50"
                    aria-label={report.isFavorite ? t('manager.modules.claudeQuery.reports.removeFavorite', {}, 'Fjern favoritt') : t('manager.modules.claudeQuery.reports.markFavorite', {}, 'Merk som favoritt')}
                    aria-pressed={!!report.isFavorite}
                >
                    <Star
                        size={16}
                        className={report.isFavorite
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300 hover:text-yellow-400'
                        }
                    />
                </button>
            </div>

            {/* Hva rapporten viser */}
            {view && (
                <p className="text-xs text-gray-500 mb-2">
                    {t('manager.modules.claudeQuery.reports.viewSummary', { '0': view.panels.length }, `Compass-visning · ${view.panels.length} deler`)}
                </p>
            )}
            {description && (
                <p className="text-sm text-gray-500 mb-3 line-clamp-2">{description}</p>
            )}

            {/* Category + Schedule badge */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                {report.category && (
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                        {report.category}
                    </span>
                )}
                {report.scheduleEnabled && (
                    <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Calendar size={10} />
                        {t('manager.modules.claudeQuery.common.scheduled', {}, 'Schedulert')}
                    </span>
                )}
            </div>

            {/* Last run info */}
            {report.lastRunAt && (
                <div className="text-xs text-gray-400 flex items-center gap-1 mb-3">
                    {getStatusIcon()}
                    <span>{t('manager.modules.claudeQuery.reports.lastRun', { '0': formatDate(report.lastRunAt) ?? '' }, `Siste kjøring: ${formatDate(report.lastRunAt)}`)}</span>
                    {report.lastRunRowCount != null && (
                        <span className="text-gray-300">{t('manager.modules.claudeQuery.reports.rowCountParen', { '0': report.lastRunRowCount }, `(${report.lastRunRowCount} rader)`)}</span>
                    )}
                </div>
            )}

            {/* Én hovedhandling; kjøring (kan sende e-post), redigering og sletting i menyen */}
            <div className="flex items-center gap-2 pt-2 mt-auto border-t border-gray-100">
                {onOpen && (
                    <button
                        onClick={() => onOpen(report)}
                        className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] px-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                    >
                        <Eye size={16} />
                        {t('manager.modules.claudeQuery.view.open', {}, 'Åpne')}
                    </button>
                )}
                {isRunning && (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Loader2 size={14} className="animate-spin" />
                        {t('manager.modules.claudeQuery.reports.running', {}, 'Kjører...')}
                    </span>
                )}
                <div className="relative" ref={menuRef}>
                    <button
                        onClick={() => setMenuOpen(o => !o)}
                        aria-expanded={menuOpen}
                        aria-haspopup="menu"
                        aria-label={t('manager.modules.claudeQuery.reports.moreActions', {}, 'Flere valg')}
                        className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
                    >
                        <MoreHorizontal size={18} />
                    </button>
                    {menuOpen && (
                        <div role="menu" className="absolute right-0 bottom-full mb-1 z-20 w-60 bg-white border border-gray-200 rounded-lg shadow-lg p-1">
                            <button
                                role="menuitem"
                                onClick={() => { setMenuOpen(false); onRun(report.reportId); }}
                                disabled={isRunning}
                                className={`${menuItem} text-gray-700 disabled:opacity-50`}
                            >
                                <Play size={16} className="flex-shrink-0" />
                                <span>
                                    <span className="block">{t('manager.modules.claudeQuery.reports.runNow', {}, 'Kjør nå')}</span>
                                    <span className="block text-xs text-gray-400">
                                        {report.deliveryMethod === 'email'
                                            ? t('manager.modules.claudeQuery.reports.runNowEmailHint', {}, 'Lager fil og sender e-post')
                                            : t('manager.modules.claudeQuery.reports.runNowHint', {}, 'Lager eksportfil etter oppsettet')}
                                    </span>
                                </span>
                            </button>
                            <button role="menuitem" onClick={() => { setMenuOpen(false); onEdit(report); }} className={`${menuItem} text-gray-700`}>
                                <Edit size={16} className="flex-shrink-0" />
                                {t('common.buttons.edit', {}, 'Rediger')}
                            </button>
                            <button role="menuitem" onClick={() => { setMenuOpen(false); onDelete(report.reportId); }} className={`${menuItem} text-red-600`}>
                                <Trash2 size={16} className="flex-shrink-0" />
                                {t('common.buttons.delete', {}, 'Slett')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
